/* eslint-disable */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Checkbox, Form, Input, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { fetchSystemRolesList } from '@/redux/use/systemRoleSlice'
import { fetchSystemPermissionsList } from '@/redux/use/systemPermissionSlice'
import { fetchSystemAgentRoles, resetAgentRolesState } from '@/redux/use/systemAgentSlice'
import {
  enrichRolePermissionsWithAncestors,
  groupPermissionNodesThreeLevels,
  pickRolePermissionNodes
} from '@/utils/rolePermissionTree'

const { Text } = Typography

function permNodeLabel(n) {
  if (!n) return ''
  if (n.name != null && n.name !== '') {
    return n.code != null && n.code !== '' ? `${n.name} (${n.code})` : String(n.name)
  }
  return String(n.id)
}

function filterPermissionNodesByKeyword(nodes, keyword) {
  const kw = (keyword || '').trim().toLowerCase()
  if (!kw || !nodes?.length) return nodes || []
  return nodes.filter((n) => {
    const name = String(n.name || '').toLowerCase()
    const code = String(n.code || '').toLowerCase()
    const id = String(n.id ?? '')
    return name.includes(kw) || code.includes(kw) || id.includes(kw)
  })
}

const mapStateToProps = (state) => ({
  rolesList: state.use.systemRole.list,
  rolesFetching: state.use.systemRole.fetching,
  permissionsList: state.use.systemPermission.list,
  permissionsFetching: state.use.systemPermission.fetching,
  agentRoles: state.use.systemAgent.agentRoles
})

const mapDispatchToProps = {
  fetchSystemRolesList,
  fetchSystemPermissionsList,
  fetchSystemAgentRoles,
  resetAgentRolesState
}

function roleIdsFromRecord(record) {
  if (!record) return []
  const fromStr = record.roleIds
  if (typeof fromStr === 'string' && fromStr.trim() !== '') {
    return fromStr
      .split(',')
      .map((s) => s.trim())
      .filter((id) => id !== '')
  }
  const roles = record.roles
  if (!Array.isArray(roles)) return []
  return roles.map((r) => String(r.id)).filter((id) => id !== '')
}

/**
 * 解析 GET /system/agents/{id}/roles 返回体（兼容数组、逗号串、{ roleIds }、{ roles }）
 * @returns {string[]|null} 无法识别时返回 null，由调用方决定是否回退行数据
 */
function roleIdsFromApiData(data) {
  if (data == null) return []
  if (Array.isArray(data)) {
    if (data.length === 0) return []
    const first = data[0]
    if (first != null && typeof first === 'object' && 'id' in first) {
      return data.map((r) => String(r.id)).filter((id) => id !== '')
    }
    return data.map((x) => String(x)).filter((id) => id !== '')
  }
  if (typeof data === 'string' && data.trim() !== '') {
    return data
      .split(',')
      .map((s) => s.trim())
      .filter((id) => id !== '')
  }
  if (typeof data === 'object') {
    if (typeof data.roleIds === 'string' && data.roleIds.trim() !== '') {
      return roleIdsFromRecord({ roleIds: data.roleIds })
    }
    if (Array.isArray(data.roleIds)) {
      return data.roleIds.map((id) => String(id)).filter((id) => id !== '')
    }
    if (Array.isArray(data.roles)) {
      return data.roles.map((r) => String(r.id)).filter((id) => id !== '')
    }
  }
  return null
}

/**
 * GET /system/agents/{id}/roles 若返回完整角色对象（含 permissions），直接取用；否则走 id 与全量角色列表对齐
 */
function rolesArrayFromAgentRolesData(data) {
  if (data == null) return []
  if (Array.isArray(data)) {
    if (data.length === 0) return []
    const first = data[0]
    if (first != null && typeof first === 'object' && !Array.isArray(first)) {
      return data.filter((r) => r && typeof r === 'object')
    }
    return []
  }
  if (typeof data === 'object' && Array.isArray(data.roles)) {
    const arr = data.roles
    if (arr.length === 0) return []
    const f = arr[0]
    if (f != null && typeof f === 'object' && !Array.isArray(f)) {
      return arr.filter((r) => r && typeof r === 'object')
    }
  }
  return []
}

function getRolesForPermissionAggregation(agentRolesData, record, rolesList) {
  const fromApi = rolesArrayFromAgentRolesData(agentRolesData)
  if (fromApi.length > 0) return fromApi
  let ids = roleIdsFromApiData(agentRolesData)
  if (ids == null || ids.length === 0) ids = roleIdsFromRecord(record)
  return ids.map((id) => {
    const full = (rolesList || []).find((r) => String(r.id) === String(id))
    return full || { id }
  })
}

/** 合并多角色下的权限节点，按 id 去重 */
function aggregatePermissionNodesForAgent(roles, rolesList) {
  const byId = new Map()
  for (const r of roles || []) {
    const roleObj =
      r?.id != null && rolesList?.length
        ? rolesList.find((x) => String(x.id) === String(r.id)) || r
        : r
    for (const node of pickRolePermissionNodes(roleObj)) {
      const k = String(node.id)
      if (!byId.has(k)) byId.set(k, node)
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const na = Number(a.id)
    const nb = Number(b.id)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
    return String(a.id).localeCompare(String(b.id))
  })
}

/**
 * 为员工分配系统角色（WrappedModal 注入 form ref）
 * 已选角色以 GET /system/agents/{agentId}/roles 为准；失败时回退列表行上的 roleIds/roles。
 */
class AgentAssignRolesForm extends React.Component {
  state = {
    /** 仅用于筛选列表，不入表单 */
    roleFilter: '',
    /** 已绑定权限区：本地筛选关键词 */
    permFilter: ''
  }

  static propTypes = {
    form: PropTypes.object,
    rolesList: PropTypes.array,
    rolesFetching: PropTypes.bool,
    fetchSystemRolesList: PropTypes.func,
    fetchSystemPermissionsList: PropTypes.func,
    permissionsList: PropTypes.array,
    permissionsFetching: PropTypes.bool,
    fetchSystemAgentRoles: PropTypes.func,
    resetAgentRolesState: PropTypes.func,
    agentRoles: PropTypes.object,
    record: PropTypes.object
  }

  componentDidMount() {
    this.loadDependencies()
  }

  componentWillUnmount() {
    const { resetAgentRolesState: reset } = this.props
    reset()
  }

  componentDidUpdate(prevProps) {
    const { record, agentRoles } = this.props
    const prevRecord = prevProps.record
    if ((record?.id ?? null) !== (prevRecord?.id ?? null)) {
      this.setState({ permFilter: '' })
      this.loadDependencies()
      return
    }
    const prevAr = prevProps.agentRoles
    if (
      prevAr.fetching === true &&
      agentRoles.fetching === false &&
      String(agentRoles.query.agentId ?? '') === String(record?.id ?? '')
    ) {
      this.applyInitial()
    }
  }

  loadDependencies = () => {
    const {
      fetchSystemRolesList: loadRoles,
      fetchSystemPermissionsList: loadPerm,
      fetchSystemAgentRoles: loadBound,
      permissionsList,
      record
    } = this.props
    const agentId = record?.id
    const pRoles = loadRoles({ include: 'permissions,users' })
    const pBound =
      agentId != null && agentId !== ''
        ? loadBound({ query: { agentId } })
        : Promise.resolve()
    const pPerm =
      !permissionsList || permissionsList.length === 0 ? loadPerm() : Promise.resolve()
    Promise.all([pRoles, pBound, pPerm]).finally(() => this.applyInitial())
  }

  applyInitial = () => {
    const { record, form, agentRoles } = this.props
    if (!form?.current) return
    const agentId = record?.id
    let roleIds = roleIdsFromRecord(record)
    const sameAgent =
      agentId != null &&
      String(agentRoles.query.agentId ?? '') === String(agentId)
    if (sameAgent && !agentRoles.fetching && agentRoles.error == null) {
      const fromApi = roleIdsFromApiData(agentRoles.data)
      if (fromApi != null) {
        roleIds = fromApi
      }
    }
    form.current.setFieldsValue({
      roleIds
    })
  }

  renderPermissionTooltipTitle = (node) => (
    <div style={{ fontSize: 12 }}>
      <div>{node?.name ?? '—'}</div>
      {node?.code != null && node.code !== '' ? (
        <div style={{ marginTop: 4, opacity: 0.88 }}>编码：{node.code}</div>
      ) : null}
      <div style={{ fontFamily: 'monospace', marginTop: 4, opacity: 0.75 }}>id：{node?.id}</div>
    </div>
  )

  renderPermissionGroupBlock = (g) => {
    const borderRadiusSM = 6
    const colorFillQuaternary = 'rgba(0, 0, 0, 0.04)'
    const colorPrimary = 'var(--ant-color-primary, #1677ff)'
    return (
      <div
        key={g.key}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 6,
          minWidth: 0,
          width: '100%',
          padding: '6px 8px',
          borderRadius: borderRadiusSM,
          background: colorFillQuaternary,
          borderLeft: `3px solid ${colorPrimary}`
        }}
      >
        <Text strong style={{ fontSize: 12, lineHeight: 1.35 }}>
          {g.root.name}
        </Text>
        {!g.level2.length ? (
          <Tooltip title={this.renderPermissionTooltipTitle(g.root)}>
            <Tag style={{ marginInlineEnd: 0 }}>{permNodeLabel(g.root)}</Tag>
          </Tooltip>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              width: '100%',
              paddingLeft: 2
            }}
          >
            {g.level2.map((l2) => (
              <div
                key={l2.node.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {l2.level3.length ? (
                  <>
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                      {l2.node.name}
                    </Text>
                    {l2.level3.map((l3) => (
                      <Tooltip key={l3.id} title={this.renderPermissionTooltipTitle(l3)}>
                        <Tag style={{ marginInlineEnd: 0 }}>{permNodeLabel(l3)}</Tag>
                      </Tooltip>
                    ))}
                  </>
                ) : (
                  <Tooltip title={this.renderPermissionTooltipTitle(l2.node)}>
                    <Tag style={{ marginInlineEnd: 0 }}>{permNodeLabel(l2.node)}</Tag>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  renderPermissionBlocks = (permissionNodes, permLoading) => {
    const { permFilter } = this.state
    const filtered = filterPermissionNodesByKeyword(permissionNodes, permFilter)
    const groups = groupPermissionNodesThreeLevels(filtered)
    const flatFallback = filtered.length > 0 && groups.length === 0

    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 8
          }}
        >
          <Text type="secondary">已绑定权限</Text>
          {!permLoading && permissionNodes.length > 0 ? (
            <Tag color="processing">{permissionNodes.length} 项</Tag>
          ) : null}
          {!permLoading && permissionNodes.length > 0 ? (
            <Input
              allowClear
              size="small"
              placeholder="按名称、编码筛选"
              value={permFilter}
              onChange={(e) => this.setState({ permFilter: e.target.value })}
              style={{ width: 200, maxWidth: '100%' }}
            />
          ) : null}
        </div>
        <div
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            padding: '10px 12px',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fafafa'
          }}
        >
          {permLoading ? (
            <Text type="secondary">加载中…</Text>
          ) : permissionNodes.length === 0 ? (
            <Text type="secondary">暂无权限数据（所选角色未配置权限或仍在加载）</Text>
          ) : filtered.length === 0 ? (
            <Text type="secondary">无匹配权限，请调整筛选条件或清空搜索框</Text>
          ) : flatFallback ? (
            <Space wrap size={[4, 8]}>
              {filtered.map((n) => (
                <Tooltip key={String(n.id)} title={this.renderPermissionTooltipTitle(n)}>
                  <Tag style={{ marginInlineEnd: 0 }}>{permNodeLabel(n)}</Tag>
                </Tooltip>
              ))}
            </Space>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
                gap: 8,
                width: '100%',
                minWidth: 0,
                alignItems: 'start'
              }}
            >
              {groups.map((g) => this.renderPermissionGroupBlock(g))}
            </div>
          )}
        </div>
      </>
    )
  }

  render() {
    const {
      form,
      rolesList,
      rolesFetching,
      permissionsList,
      permissionsFetching,
      record,
      agentRoles
    } = this.props
    const { fetching: boundFetching } = agentRoles
    const { roleFilter } = this.state
    const keyword = (roleFilter || '').trim().toLowerCase()

    const roleRows = (rolesList || []).map((r) => {
      const text =
        r.name != null && r.name !== ''
          ? `${r.name}${r.code != null && r.code !== '' ? ` (${r.code})` : ''}`
          : String(r.code ?? r.id)
      return {
        value: String(r.id),
        label: text,
        searchText: `${text} ${r.code ?? ''} ${r.id}`.toLowerCase()
      }
    })

    /** 筛选仅控制展示；Checkbox 必须始终挂载全部选项，否则 Group 会丢掉未渲染项的已选 value */
    const rowMatchesFilter = (row) =>
      keyword === '' || row.searchText.includes(keyword)
    const visibleRoleCount = roleRows.filter(rowMatchesFilter).length

    const agentId = record?.id
    const sameAgent =
      agentId != null &&
      String(agentRoles.query.agentId ?? '') === String(agentId)
    const agentRolesData =
      sameAgent && !agentRoles.fetching && agentRoles.error == null ? agentRoles.data : null
    const rolesForPerm = getRolesForPermissionAggregation(agentRolesData, record, rolesList)
    const rawPermNodes = aggregatePermissionNodesForAgent(rolesForPerm, rolesList)
    const permissionNodes =
      permissionsList && permissionsList.length
        ? enrichRolePermissionsWithAncestors(rawPermNodes, permissionsList)
        : rawPermNodes
    const permLoading =
      (rolesFetching || boundFetching || permissionsFetching) && permissionNodes.length === 0

    return (
      <Form
        ref={form}
        layout="vertical"
        initialValues={{ roleIds: [] }}
        style={{ width: '100%' }}
      >
        {record ? (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              账号：{record.account ?? '—'}　用户名：{record.userName ?? '—'}
            </Text>
            <div style={{ marginBottom: 12 }}>{this.renderPermissionBlocks(permissionNodes, permLoading)}</div>
          </>
        ) : null}
        <Form.Item
          label="系统角色"
          tooltip="全量替换该员工的系统角色；取消勾选并保存可移除对应角色。已选角色与后台实时同步。"
        >
          <Spin spinning={rolesFetching || boundFetching || permissionsFetching}>
            <Input
              allowClear
              placeholder="筛选角色（名称、编码）"
              value={roleFilter}
              onChange={(e) => this.setState({ roleFilter: e.target.value })}
              style={{ marginBottom: 8 }}
            />
            <div
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                padding: '8px 12px',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                background: '#fafafa'
              }}
            >
              {roleRows.length === 0 ? (
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  暂无角色数据
                </Text>
              ) : keyword !== '' && visibleRoleCount === 0 ? (
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  无匹配角色；已勾选项仍保留，清空搜索可查看全部
                </Text>
              ) : null}
              {roleRows.length > 0 ? (
                <Form.Item name="roleIds" noStyle rules={[{ required: false }]}>
                  <Checkbox.Group style={{ width: '100%' }}>
                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                      {roleRows.map((row) => {
                        const visible = rowMatchesFilter(row)
                        return (
                          <Checkbox
                            key={row.value}
                            value={row.value}
                            style={{
                              alignItems: 'flex-start',
                              lineHeight: 1.5,
                              display: visible ? 'flex' : 'none'
                            }}
                          >
                            {row.label}
                          </Checkbox>
                        )
                      })}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              ) : null}
            </div>
          </Spin>
        </Form.Item>
      </Form>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AgentAssignRolesForm)
